import { useEffect } from "react";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import type { InboxNotification, ListNotificationsResponse, MarkNotificationsReadRequest } from "@rivets/core";
import { client } from "@/lib/api";
import { qk } from "@/lib/query-keys";

/**
 * The inbox on the web: the member's mentions, newest first, with an unread
 * count in the sidebar, and — when they opt in — a desktop notification for
 * each new one while this tab is in the background.
 *
 * The first page lives in the react-query cache under `qk.inbox(orgId)`. The
 * socket's `notification.created` and `notifications.updated` frames patch it
 * in place, so the badge moves without a refetch; anything they miss is put
 * right the next time the inbox is fetched. Reading is the server's, so a
 * mention read on a phone clears here too.
 */

export const INBOX_PAGE_SIZE = 50;

export function useInbox(orgId: string | undefined) {
  return useQuery({
    queryKey: qk.inbox(orgId ?? ""),
    queryFn: ({ signal }) => client.withSignal(signal).notifications(orgId!, { limit: INBOX_PAGE_SIZE }),
    enabled: Boolean(orgId),
    staleTime: 60_000,
  });
}

/**
 * The cache after an entry arrived. An entry the cache already has is one
 * that grew — a gathered `prompt` or `discussion` with one more — so it is
 * replaced and moves to the top; the same frame twice changes nothing.
 */
export function withCreated(data: ListNotificationsResponse, entry: InboxNotification): ListNotificationsResponse {
  const existing = data.notifications.find((known) => known.id === entry.id);
  if (existing && existing.createdAt === entry.createdAt && existing.count === entry.count) return data;
  const wasUnread = existing ? existing.readAt === null : false;
  const isUnread = entry.readAt === null;
  return {
    ...data,
    notifications: [entry, ...data.notifications.filter((known) => known.id !== entry.id)],
    unreadCount: data.unreadCount + (isUnread ? 1 : 0) - (wasUnread ? 1 : 0),
  };
}

/** One line saying what an entry is about, for the inbox row and a desktop
 * notification. `actor` is the name of whoever caused it. */
export function inboxHeadline(entry: InboxNotification, actor: string): { lead: string; verb: string } {
  const more = entry.count > 1 ? ` (${entry.count})` : "";
  switch (entry.kind) {
    case "mention": return { lead: actor, verb: "mentioned you in" };
    case "run.finished": return { lead: entry.jobStatus === "failed" ? "Run failed" : "Run completed", verb: "in" };
    case "prompt": return { lead: actor, verb: `directed the agent${more} in` };
    case "discussion": return { lead: actor, verb: `talked to the team${more} in` };
  }
}

/** The cache after entries were read or removed. The count only moves for
 * entries this page knew were unread; one beyond the page is corrected by the
 * next fetch. */
export function withUpdated(data: ListNotificationsResponse, read: readonly string[], removed: readonly string[], now = Date.now()): ListNotificationsResponse {
  if (read.length === 0 && removed.length === 0) return data;
  const readIds = new Set(read);
  const removedIds = new Set(removed);
  let unread = data.unreadCount;
  const notifications: InboxNotification[] = [];
  for (const entry of data.notifications) {
    const wasUnread = entry.readAt === null;
    if (removedIds.has(entry.id)) {
      if (wasUnread) unread -= 1;
      continue;
    }
    if (wasUnread && readIds.has(entry.id)) {
      unread -= 1;
      notifications.push({ ...entry, readAt: now });
      continue;
    }
    notifications.push(entry);
  }
  return { ...data, notifications, unreadCount: Math.max(0, unread) };
}

/** Unread entries in this page that are about `chatId`. */
export function unreadForChat(data: ListNotificationsResponse | undefined, chatId: string): string[] {
  return data?.notifications.filter((entry) => entry.chatId === chatId && entry.readAt === null).map((entry) => entry.id) ?? [];
}

/** Marks entries read: drawn read at once, put right if the server refuses. */
export async function markInboxRead(queryClient: QueryClient, orgId: string, request: MarkNotificationsReadRequest): Promise<void> {
  const key = qk.inbox(orgId);
  const data = queryClient.getQueryData<ListNotificationsResponse>(key);
  if (data) {
    const ids = "ids" in request ? request.ids
      : "chatId" in request ? unreadForChat(data, request.chatId)
      : data.notifications.filter((entry) => entry.readAt === null).map((entry) => entry.id);
    const next = withUpdated(data, ids, []);
    queryClient.setQueryData<ListNotificationsResponse>(key, "all" in request ? { ...next, unreadCount: 0 } : next);
  }
  try {
    await client.markNotificationsRead(orgId, request);
  } catch {
    await queryClient.invalidateQueries({ queryKey: key });
  }
}

// -- what the reader is looking at -------------------------------------------

type ActiveChatState = { chatId: string | null; set: (chatId: string | null) => void };

/** The chat on screen, so a mention in it is read rather than announced. */
export const useActiveChat = create<ActiveChatState>()((set) => ({
  chatId: null,
  set: (chatId) => set({ chatId }),
}));

function readerIsLookingAt(chatId: string): boolean {
  return typeof document !== "undefined" && document.visibilityState === "visible" && useActiveChat.getState().chatId === chatId;
}

/**
 * While `chatId` is on screen and the tab is visible, its mentions are read:
 * on opening it, on coming back to the tab, and as new ones arrive.
 */
export function useReadMentionsInChat(queryClient: QueryClient, orgId: string, chatId: string): void {
  const { data } = useInbox(orgId);
  const unread = unreadForChat(data, chatId).length;
  useEffect(() => {
    useActiveChat.getState().set(chatId);
    return () => { if (useActiveChat.getState().chatId === chatId) useActiveChat.getState().set(null); };
  }, [chatId]);
  useEffect(() => {
    const read = () => {
      if (!readerIsLookingAt(chatId)) return;
      const current = queryClient.getQueryData<ListNotificationsResponse>(qk.inbox(orgId));
      if (unreadForChat(current, chatId).length > 0) void markInboxRead(queryClient, orgId, { chatId });
    };
    read();
    document.addEventListener("visibilitychange", read);
    return () => document.removeEventListener("visibilitychange", read);
  }, [queryClient, orgId, chatId, unread]);
}

// -- frames from the socket ---------------------------------------------------

/** Where a desktop notification's click goes: set by the shell, which owns
 * the router. */
let openEntry: ((entry: InboxNotification) => void) | null = null;
export function setInboxOpener(open: ((entry: InboxNotification) => void) | null): void {
  openEntry = open;
}

export function applyInboxCreated(queryClient: QueryClient, orgId: string, entry: InboxNotification): void {
  if (entry.orgId !== orgId) return;
  const key = qk.inbox(orgId);
  if (queryClient.getQueryData(key)) {
    queryClient.setQueryData<ListNotificationsResponse>(key, (data) => (data ? withCreated(data, entry) : data));
  } else {
    void queryClient.invalidateQueries({ queryKey: key });
  }
  // Said in the chat the reader is looking at: they have read it.
  if (readerIsLookingAt(entry.chatId)) {
    void markInboxRead(queryClient, orgId, { ids: [entry.id] });
    return;
  }
  showDesktopNotification(entry);
}

export function applyInboxUpdated(queryClient: QueryClient, orgId: string, read: readonly string[], removed: readonly string[]): void {
  queryClient.setQueryData<ListNotificationsResponse>(qk.inbox(orgId), (data) => (data ? withUpdated(data, read, removed) : data));
  for (const id of [...read, ...removed]) closeDesktopNotification(id);
}

// -- desktop notifications ----------------------------------------------------

const DESKTOP_KEY = "rivets.desktop-notifications.v1";

/** The reader's own switch, on top of the browser's permission: granting it
 * once for the site should not mean every tab announces forever. */
type DesktopState = { enabled: boolean; permission: NotificationPermission | "unsupported"; enable: () => Promise<void>; disable: () => void };

function loadEnabled(): boolean {
  try { return typeof window !== "undefined" && window.localStorage.getItem(DESKTOP_KEY) === "on"; } catch { return false; }
}
function saveEnabled(enabled: boolean): void {
  try { window.localStorage.setItem(DESKTOP_KEY, enabled ? "on" : "off"); } catch { /* the in-memory switch still works */ }
}
function currentPermission(): NotificationPermission | "unsupported" {
  return typeof window === "undefined" || !("Notification" in window) ? "unsupported" : Notification.permission;
}

export const useDesktopNotifications = create<DesktopState>()((set) => ({
  enabled: loadEnabled(),
  permission: currentPermission(),
  enable: async () => {
    if (currentPermission() === "unsupported") return;
    const permission = Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
    const enabled = permission === "granted";
    saveEnabled(enabled);
    set({ enabled, permission });
  },
  disable: () => {
    saveEnabled(false);
    set({ enabled: false });
  },
}));

const shown = new Map<string, Notification>();

/** Whether a new entry should become a desktop notification: only when the
 * reader asked for them, the browser allows them, and they are not already
 * looking at this tab. Exported for tests. */
export function shouldAnnounce(input: { enabled: boolean; permission: string; visible: boolean }): boolean {
  return input.enabled && input.permission === "granted" && !input.visible;
}

function showDesktopNotification(entry: InboxNotification): void {
  const { enabled, permission } = useDesktopNotifications.getState();
  if (!shouldAnnounce({ enabled, permission, visible: document.visibilityState === "visible" })) return;
  try {
    const headline = inboxHeadline(entry, "A teammate");
    const title = entry.kind === "mention" ? `Mentioned in ${entry.taskName}`
      : entry.kind === "run.finished" ? `${headline.lead} in ${entry.taskName}`
      : `${entry.kind === "prompt" ? "Agent directed" : "Team chat"} in ${entry.taskName}`;
    const notification = new Notification(title, {
      body: entry.excerpt,
      tag: entry.id,
    });
    notification.onclick = () => {
      window.focus();
      openEntry?.(entry);
      notification.close();
    };
    notification.onclose = () => shown.delete(entry.id);
    shown.set(entry.id, notification);
  } catch {
    // Some browsers only allow notifications from a service worker; the
    // inbox and its badge still have it.
  }
}

function closeDesktopNotification(id: string): void {
  shown.get(id)?.close();
  shown.delete(id);
}
